import { useCallback, useState } from 'react';
import type { ComponentProps } from 'react';

import type { ADFRenderer } from '@confluence/adf-renderer';
import { useProgressiveRenderingConfig } from '@confluence/complex-page/entry-points/useProgressiveRenderingConfig';
import { fg } from '@confluence/feature-gating';

import { AboveTheFoldReactSerializer } from './AboveTheFoldReactSerializer';
import { ProgressiveReactSerializer } from './ProgressiveReactSerializer';

export const useCreateSerializer = (props: Parameters<typeof useProgressiveRenderingConfig>[0]) => {
	const { skip } = props;

	const { isComplexPage, isComplexPageReason, batchSize } = useProgressiveRenderingConfig(props);

	// Initially, only serialize the first batch. This is a performance optimization to delay
	// serialization of the remaining batches until later. The optimization is disabled if there
	// are not enough batches to warrant it.
	//
	// For SSR, subsequent serializations never happen.
	// For SPA, subsequent serializations happen after the first batch is rendered.
	const [onlySerializeFirstBatch, setOnlySerializeFirstBatch] = useState(
		fg('cc_complexit_fe_progessive_rendering_ssr_serial'),
	);

	/**
	 * Creates a new `Serializer` that progressively renders ADF as described at https://hello.atlassian.net/wiki/spaces/~lmarinov/pages/5177285037/COMPLEXIT+Progressive+rendering+of+ADF.
	 */
	const createSerializer: ComponentProps<typeof ADFRenderer>['createSerializer'] = useCallback(
		(init) => {
			const onBatchRendered = (done: boolean) => {
				if (!done) {
					setOnlySerializeFirstBatch(false);
				}
			};

			if (isComplexPageReason === 'TooManyExtensions') {
				return new AboveTheFoldReactSerializer(init, onlySerializeFirstBatch, onBatchRendered);
			} else {
				return new ProgressiveReactSerializer(
					init,
					batchSize,
					onlySerializeFirstBatch,
					onBatchRendered,
				);
			}
		},
		[batchSize, isComplexPageReason, onlySerializeFirstBatch, setOnlySerializeFirstBatch],
	);

	return !skip && isComplexPage && fg('cc_complexit_fe_progressive_adf_rendering')
		? createSerializer
		: undefined;
};
